import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";

import { NotificationsResponse } from "../types";

type WSMessage =
  | { type: "UPDATE_NOTIFICATIONS"; data: NotificationsResponse }
  | { type: "PONG" };

type IncomingMessage = { type: "HEARTBEAT" };

interface UseUserWebSocketProps {
  user: any; // Clerk user object
  onNotificationsUpdate: (notifications: NotificationsResponse) => void;
}

export const useUserWebSocket = ({
  user,
  onNotificationsUpdate,
}: UseUserWebSocketProps) => {
  const { getToken } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const heartbeatIntervalRef = useRef<number | null>(null);

  const callbacksRef = useRef({ onNotificationsUpdate });
  useEffect(() => {
    callbacksRef.current = { onNotificationsUpdate };
  }, [onNotificationsUpdate]);

  const connect = useCallback(async () => {
    if (!user) return;

    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    )
      return;

    try {
      let apiBase = import.meta.env.VITE_API_URL || "http://localhost:8787/api";

      // Ensure it ends with /api if it looks like a raw host (common mistake)
      if (!apiBase.endsWith("/api")) {
        apiBase = `${apiBase}/api`;
      }

      const hostPath = apiBase.replace(/^https?:\/\//, "");
      const wsProtocol = apiBase.startsWith("https") ? "wss" : "ws";

      const token = await getToken();
      const wsUrl = `${wsProtocol}://${hostPath}/notifications/ws?__clerk_token=${token}`;

      console.log("Connecting to User WebSocket...");
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        console.log("User WebSocket connected");

        if (heartbeatIntervalRef.current)
          clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "HEARTBEAT" }));
          }
        }, 300000); // 5 minutes
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WSMessage;
          const { onNotificationsUpdate } = callbacksRef.current;

          switch (msg.type) {
            case "UPDATE_NOTIFICATIONS":
              onNotificationsUpdate(msg.data);
              break;
            case "PONG":
              break;
          }
        } catch (e) {
          console.error("Error parsing User WS message", e);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        console.log(
          `User WebSocket disconnected: ${event.code} ${event.reason}`,
        );
        if (heartbeatIntervalRef.current)
          clearInterval(heartbeatIntervalRef.current);
        wsRef.current = null;

        if (event.code !== 1000) {
          setTimeout(connect, 3000);
        }
      };

      ws.onerror = (e) => {
        console.error("User WebSocket error", e);
        ws.close();
      };
    } catch (e) {
      console.error("Failed to connect User WebSocket", e);
    }
  }, [user?.id, getToken]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounted");
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [connect]);

  return { isConnected };
};
